import React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FieldInfo } from '../../../../components/FieldInfo';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';

// import { firebaseApp } from '../../../../constants/firebaseModular';
// import { deleteApp } from 'firebase/app';

export const Route = createFileRoute('/_authenticated/$patientId/photos/idOnly')({
  component: IdSelf,
});

function IdSelf() {
  const navigate = useNavigate();
  const { mutate } = useUpdateChartSection();
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [warned, setWarned] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);

  // DELETING FIREBASE APP IN THIS WAY CAUSES FIREBASE TO THROW AN ERROR {"code":"aborted","name":"FirebaseError"}
  // React.useEffect(() => {
  //   const unloadCallback = () => {
  //     deleteApp(firebaseApp);
  //   };
  //   window.addEventListener('beforeunload', unloadCallback);
  //   return () => {
  //     window.removeEventListener('beforeunload', unloadCallback);
  //   };
  // }, []);

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({
        currentPageName: 'idOnly',
        patientInfo,
        value: true,
      });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'idOnly',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_ID: {
            issuingState: idForm.state.values.issuingState,
            licenseNumber: idForm.state.values.idNumber,

            photoIdentificationFrontURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
            photoIdentificationBackURI: chart?.PHOTO_ID?.photoIdentificationBackURI
              ? chart.PHOTO_ID.photoIdentificationBackURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setIsSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // CAPTURE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const idForm = useForm({
    defaultValues: {
      issuingState: chart?.PHOTO_ID?.issuingState ? chart.PHOTO_ID.issuingState : '',
      idNumber: chart?.PHOTO_ID?.licenseNumber ? chart?.PHOTO_ID.licenseNumber : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="photo-upload-page" className="flex flex-col items-center">
          <Heading id="photo-upload-page-header" textContent="Identification Upload" />
          <p className="px-2 text-xl">
            To get started, you will need to upload a copy of your government issued ID. MIDEO personnel will use this
            photo to match your identity with your MIDEO safety statement. We also perform an automated scan of this
            image to identify form information we can enter on your behalf.
          </p>
          <p className="mt-2 px-2 text-xl">
            Please wait until the card text is clear in the image preview before taking the photo.
          </p>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <p className="text-2xl font-bold">Government issued ID (front):</p>
              {chart?.PHOTO_ID?.photoIdentificationFrontURI && takingPhoto.idFront === false ? (
                <div className="flex w-full flex-row items-center justify-around">
                  <div id="photo-container" className="flex w-2/3 flex-col items-center p-4 lg:w-1/3">
                    <img src={chart?.PHOTO_ID?.photoIdentificationFrontURI} alt="ID Front" />
                    <p className="text-xl">Photo on File</p>
                  </div>
                  <div className="ml-2 flex flex-col items-center">
                    <Button
                      id="retake-gov-id-front"
                      type="button"
                      textContent="Retake Photo with Webcam"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                  </div>
                  {!isMobile && (
                    <Button
                      id="retake-gov-id-front"
                      type="button"
                      textContent="Retake Photo with Phone"
                      onClick={() => {
                        navigate({ to: `/${patientId}/photos/qrInstructions` });
                      }}
                    />
                  )}
                </div>
              ) : (
                takingPhoto.idFront === false && (
                  <div className="flex w-full flex-row items-center justify-around">
                    <Button
                      id="take-gov-id-front"
                      type="button"
                      textContent="Take Photo with Webcam"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                    {!isMobile && (
                      <Button
                        id="take-gov-id-front"
                        type="button"
                        textContent="Take Photo with Phone"
                        onClick={() => {
                          navigate({ to: `/${patientId}/photos/qrInstructions` });
                        }}
                      />
                    )}
                  </div>
                )
              )}
              {takingPhoto.idFront && (
                <PhotoCamera
                  type={FileTypes.idFront}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationFront`}
                  patientId={patientId}
                  hideCamera={() => {
                    setTakingPhoto({ ...takingPhoto, idFront: false });
                    setPhotoOneWorking(false);
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
            <div id="rear-id-container" className="flex flex-col items-center">
              <p className="text-2xl font-bold">Government Issued ID (rear):</p>
              {chart?.PHOTO_ID?.photoIdentificationBackURI && takingPhoto.idBack === false ? (
                <div className="flex w-full flex-row items-center justify-around">
                  <div id="photo-container" className="flex w-2/3 flex-col items-center p-4 lg:w-1/3">
                    <img src={chart?.PHOTO_ID?.photoIdentificationBackURI} alt="ID Back" />
                    <p className="text-xl">Photo on File</p>
                  </div>
                  <div className="mx-2 flex flex-col items-center">
                    <Button
                      id="retake-gov-id-back"
                      type="button"
                      textContent="Retake Photo with Webcam"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idBack: true });
                      }}
                    />
                  </div>
                  {!isMobile && (
                    <Button
                      id="retake-gov-id-back"
                      type="button"
                      textContent="Retake Photo with Phone"
                      onClick={() => {
                        navigate({ to: `/${patientId}/photos/qrInstructions` });
                      }}
                    />
                  )}
                </div>
              ) : (
                takingPhoto.idBack === false && (
                  <div className="flex w-full flex-row items-center justify-around">
                    <Button
                      id="take-gov-id-back"
                      type="button"
                      textContent="Take Photo with Webcam"
                      onClick={() => setTakingPhoto({ ...takingPhoto, idBack: true })}
                    />
                    {!isMobile && (
                      <Button
                        id="take-gov-id-back"
                        type="button"
                        textContent="Take Photo with Phone"
                        onClick={() => {
                          navigate({ to: `/${patientId}/photos/qrInstructions` });
                        }}
                      />
                    )}
                  </div>
                )
              )}
              {takingPhoto.idBack && (
                <PhotoCamera
                  type={FileTypes.idBack}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationBack`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoTwoWorking(false);
                    setTakingPhoto({ ...takingPhoto, idBack: false });
                  }}
                  working={photoTwoWorking}
                  setWorking={setPhotoTwoWorking}
                />
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  idForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <idForm.Field
                    name="idNumber"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ID Number cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="ID Number"
                          id="id-number"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <idForm.Field
                    name="issuingState"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Issuing state cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Issuing State"
                          id="issuing-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
              </form>
              <div className="mt-8 flex flex-col items-center">
                <idForm.Subscribe
                  selector={(state) => [state.canSubmit, state.errors]}
                  children={([canSubmit]) => (
                    <>
                      {!canSubmit && (
                        <div className="mt-4 flex flex-col items-center">
                          <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                        </div>
                      )}
                      <Button
                        id="submit-id-info"
                        type="submit"
                        disabled={
                          !canSubmit ||
                          chart?.PHOTO_ID?.photoIdentificationFrontURI?.length < 5 ||
                          chart?.PHOTO_ID?.photoIdentificationBackURI?.length < 5
                        }
                        onClick={handleIdInfoUpdate}
                        textContent="Submit"
                        submitted={isSubmitted}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {nextPageURL &&
              chart?.PHOTO_ID?.issuingState &&
              chart?.PHOTO_ID?.licenseNumber &&
              chart?.PHOTO_ID?.photoIdentificationBackURI &&
              chart?.PHOTO_ID?.photoIdentificationFrontURI && (
                <NavForward
                  to={nextPageURL}
                  disabled={
                    !isSubmitted ||
                    !chart?.PHOTO_ID?.issuingState ||
                    !chart?.PHOTO_ID?.licenseNumber ||
                    !chart?.PHOTO_ID?.photoIdentificationFrontURI ||
                    !chart?.PHOTO_ID?.photoIdentificationBackURI
                  }
                />
              )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
